import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Tabs,
  Tab,
  Box,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Stack,
  Autocomplete,
  IconButton,
  Tooltip,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import InventoryIcon from '@mui/icons-material/Inventory';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Chart, registerables, ChartData, ChartOptions } from 'chart.js';
import { Pie, Line } from 'react-chartjs-2';
import { useLocation, useNavigate } from 'react-router-dom';

import { 
  ImportExportAnalysis as ImportExportData, 
  TimeSeriesData, 
  importExportService,
  ExportData,
  ImportData
} from '../services/importExportService';
import MarketOpportunities from '../components/market/MarketOpportunities';
import { formatNumber } from '../utils/formatters';
import { useAuth } from '../context/AuthContext';

type DetailedRow = {
  rgDesc?: string;
  TradeValue?: number;
  NetWeight?: number;
  qty?: number;
  qtDesc?: string;
  ptTitle?: string;
  cmdDesc?: string;
  Period?: string;
  partnerCode?: number;
};

// Register chart.js components
Chart.register(...registerables);

interface CommodityOption {
  label: string;
  value: string;
}

// Unit conversion multipliers
const UNITS = {
  KG: 1,
  TONNES: 0.001,
  USD: 1
};

const ImportExportAnalysis: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const initialAvailableYears = importExportService.getAvailableYears();
  const initialMaxYear = initialAvailableYears[0] ?? new Date().getFullYear() - 2;

  // Tab state
  const [tabValue, setTabValue] = useState<number>(0);
  
  // Search states
  const [commodityQuery, setCommodityQuery] = useState<string>('tea');
  const [selectedYear, setSelectedYear] = useState<number>(initialMaxYear);
  const [availableYears, setAvailableYears] = useState<number[]>(initialAvailableYears);
  const [yearRange, setYearRange] = useState<[number, number]>([
    initialMaxYear - 5,
    initialMaxYear
  ]);
  
  // Data visualization options
  const [selectedUnit, setSelectedUnit] = useState<keyof typeof UNITS>('USD');
  const [visibleTimeRange, setVisibleTimeRange] = useState<[number, number]>([0, 10]); // Visible range indexes
  
  // Data states
  const [countryData, setCountryData] = useState<ImportExportData | null>(null);
  const [commodityData, setCommodityData] = useState<any | null>(null);
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [loginPromptOpen, setLoginPromptOpen] = useState(false);
  const [detailedReportOpen, setDetailedReportOpen] = useState(false);
  const [detailedReportLoading, setDetailedReportLoading] = useState(false);
  const [detailedReportError, setDetailedReportError] = useState<string | null>(null);
  const [detailedReportPayload, setDetailedReportPayload] = useState<any | null>(null);
  
  const commodities: CommodityOption[] = [
    { label: 'Tea', value: 'tea' },
    { label: 'Coffee', value: 'coffee' },
    { label: 'Cut Flowers', value: 'cut flowers' },
    { label: 'Fresh Vegetables', value: 'fresh vegetables' },
    { label: 'Fresh Fruits', value: 'fresh fruits' },
    { label: 'Maize', value: 'maize' },
    { label: 'Wheat', value: 'wheat' },
    { label: 'Rice', value: 'rice' },
    { label: 'Beans', value: 'beans' },
    { label: 'Potatoes', value: 'potatoes' },
    { label: 'Sugarcane', value: 'sugarcane' },
    { label: 'Avocados', value: 'avocados' },
    { label: 'Mangoes', value: 'mangoes' },
    { label: 'Macadamia Nuts', value: 'macadamia nuts' },
    { label: 'Cashew Nuts', value: 'cashew nuts' },
    { label: 'Pineapples', value: 'pineapples' },
    { label: 'Sisal', value: 'sisal' },
    { label: 'Cotton', value: 'cotton' },
    { label: 'Pyrethrum', value: 'pyrethrum' },
    { label: 'Tobacco', value: 'tobacco' },
    { label: 'Dairy Products', value: 'dairy products' },
    { label: 'Meat Products', value: 'meat products' },
    { label: 'Fish Products', value: 'fish products' },
    { label: 'Leather Products', value: 'leather products' },
    { label: 'Titanium Ores', value: 'titanium ores' },
    { label: 'Petroleum Products', value: 'petroleum products' },
    { label: 'Chemicals', value: 'chemicals' },
    { label: 'Pharmaceuticals', value: 'pharmaceuticals' },
    { label: 'Textile Products', value: 'textile products' },
    { label: 'Cement', value: 'cement' },
    { label: 'Machinery', value: 'machinery' },
    { label: 'Vehicles', value: 'vehicles' },
    { label: 'Electrical Equipment', value: 'electrical equipment' }
  ];
  
  const [chartData, setChartData] = useState<any>(null);
  const [chartOptions, setChartOptions] = useState<any>(null);

  // Load initial data and generate available years
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        // Get list of available years from service
        const years = importExportService.getAvailableYears();
        setAvailableYears(years);
        
        // Load country data for the current year
        await loadCountryData(selectedYear);
        
        // Load commodity data for the initial commodity
        if (commodityQuery) {
          await loadCommodityData(commodityQuery, selectedYear);
        }
        
        // Load time series data
        await loadTimeSeriesData(yearRange[0], yearRange[1]);
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load trade data. Please try again later.');
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Update data when selected year changes
  useEffect(() => {
    if (tabValue === 0) {
      loadCountryData(selectedYear);
    } else if (tabValue === 1 && commodityQuery) {
      loadCommodityData(commodityQuery, selectedYear);
    }
  }, [selectedYear, tabValue, commodityQuery]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle search based on current tab and selected filters
  const handleSearch = () => {
    if (tabValue === 1 && commodityQuery) {
      loadCommodityData(commodityQuery, selectedYear);
      loadTimeSeriesData(yearRange[0], yearRange[1]);
    } else if (tabValue === 0 || tabValue === 2) {
      loadCountryData(selectedYear);
      loadTimeSeriesData(yearRange[0], yearRange[1]);
    }
  };

  const handleRefreshData = async () => {
    importExportService.clearCache();
    setError(null);
    if (tabValue === 1 && commodityQuery) {
      await loadCommodityData(commodityQuery, selectedYear);
      await loadTimeSeriesData(yearRange[0], yearRange[1]);
      return;
    }
    if (tabValue === 0 || tabValue === 2) {
      await loadCountryData(selectedYear);
      await loadTimeSeriesData(yearRange[0], yearRange[1]);
    }
  };

  const getDetailedReportParams = () => {
    const reporter = 'KEN';
    const partner = 'all';
    if (tabValue === 1) {
      const commodity = importExportService.getCommodityCodeForName(commodityQuery || 'TOTAL');
      return { year: selectedYear, reporter, partner, commodity };
    }
    return { year: selectedYear, reporter, partner, commodity: 'TOTAL' };
  };

  const handleDetailedReport = async (forceRefresh: boolean = false) => {
    if (!isAuthenticated) {
      setLoginPromptOpen(true);
      return;
    }

    setDetailedReportLoading(true);
    setDetailedReportError(null);
    try {
      const params = getDetailedReportParams();
      const res = await importExportService.getRawComtrade({ ...params, ...(forceRefresh ? { _ts: Date.now() } : {}) });
      setDetailedReportPayload({ params, meta: res.meta, dataset: res.dataset });
      setDetailedReportOpen(true);
    } catch (e) {
      setDetailedReportError(e instanceof Error ? e.message : String(e));
      setDetailedReportOpen(true);
    } finally {
      setDetailedReportLoading(false);
    }
  };

  const formatUsd = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

  const buildDetailedReport = (rows: DetailedRow[]) => {
    const exportsRows = rows.filter((r) => r?.rgDesc === 'Export');
    const importsRows = rows.filter((r) => r?.rgDesc === 'Import');

    const sum = (arr: DetailedRow[], field: keyof DetailedRow) =>
      arr.reduce((s, r) => s + (typeof r?.[field] === 'number' && Number.isFinite(r[field] as number) ? (r[field] as number) : 0), 0);

    const aggregateByPartner = (arr: DetailedRow[]) => {
      const map = new Map<string, { partner: string; value: number; netWeight: number; qty: number; unit: string }>();
      for (const row of arr) {
        const partner = typeof row?.ptTitle === 'string' && row.ptTitle.trim() ? row.ptTitle : 'Unknown';
        const current = map.get(partner) || { partner, value: 0, netWeight: 0, qty: 0, unit: row?.qtDesc || 'n/a' };
        current.value += typeof row?.TradeValue === 'number' && Number.isFinite(row.TradeValue) ? row.TradeValue : 0;
        current.netWeight += typeof row?.NetWeight === 'number' && Number.isFinite(row.NetWeight) ? row.NetWeight : 0;
        current.qty += typeof row?.qty === 'number' && Number.isFinite(row.qty) ? row.qty : 0;
        if (!current.unit || current.unit === 'n/a') current.unit = row?.qtDesc || current.unit;
        map.set(partner, current);
      }
      const arrOut = Array.from(map.values());
      arrOut.sort((a, b) => b.value - a.value);
      return arrOut;
    };

    const exportAgg = aggregateByPartner(exportsRows);
    const importAgg = aggregateByPartner(importsRows);

    return {
      exportsRows,
      importsRows,
      totals: {
        exportsValue: sum(exportsRows, 'TradeValue'),
        importsValue: sum(importsRows, 'TradeValue'),
        exportsNetWeight: sum(exportsRows, 'NetWeight'),
        importsNetWeight: sum(importsRows, 'NetWeight'),
      },
      topExports: exportAgg,
      topImports: importAgg,
    };
  };

  const downloadDetailedReportExcel = () => {
    if (!detailedReportPayload || !Array.isArray(detailedReportPayload.dataset)) return;
    const rows = detailedReportPayload.dataset as DetailedRow[];

    const suffix = tabValue === 1 ? commodityQuery.replace(/\s+/g, '-') : 'country';
    const filename = `import-export-report-${suffix}-${selectedYear}.csv`;

    const csvEscape = (v: unknown) => {
      const s = String(v ?? '');
      if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const header = [
      'Flow',
      'Partner',
      'PartnerCode',
      'Commodity',
      'Period',
      'ValueUSD',
      'NetWeight',
      'Qty',
      'Unit',
    ];

    const blankRow = () => new Array(header.length).fill('').join(',');
    const metaRow = (label: string, value: unknown) => {
      const cells = new Array(header.length).fill('');
      cells[0] = csvEscape(label);
      cells[1] = csvEscape(value);
      return cells.join(',');
    };

    const lines = [
      new Array(header.length).fill('').map((_, i) => (i === 0 ? csvEscape('FarmersHub') : '')).join(','),
      metaRow('Report', 'Import/Export Detailed report'),
      metaRow('Year', detailedReportPayload.params?.year),
      metaRow('Reporter', detailedReportPayload.params?.reporter),
      metaRow('Commodity', detailedReportPayload.params?.commodity),
      metaRow('Source', detailedReportPayload.meta?.source),
      metaRow('Exports status', detailedReportPayload.meta?.exportsStatus),
      metaRow('Imports status', detailedReportPayload.meta?.importsStatus),
      blankRow(),
      header.join(','),
    ];
    for (const r of rows) {
      lines.push(
        [
          csvEscape(r.rgDesc),
          csvEscape(r.ptTitle),
          csvEscape(r.partnerCode),
          csvEscape(r.cmdDesc),
          csvEscape(r.Period),
          csvEscape(typeof r.TradeValue === 'number' ? r.TradeValue : ''),
          csvEscape(typeof r.NetWeight === 'number' ? r.NetWeight : ''),
          csvEscape(typeof r.qty === 'number' ? r.qty : ''),
          csvEscape(r.qtDesc),
        ].join(','),
      );
    }

    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const printDetailedReportPdf = () => {
    if (!detailedReportPayload || !Array.isArray(detailedReportPayload.dataset)) return;
    const rows = detailedReportPayload.dataset as DetailedRow[];
    const report = buildDetailedReport(rows);
    const logoUrl = `${window.location.origin}/icon.png`;

    const esc = (s: unknown) =>
      String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const makeRows = (items: Array<{ partner: string; value: number; netWeight: number; qty: number; unit: string }>) =>
      items
        .map(
          (r) =>
            `<tr><td>${esc(r.partner)}</td><td style="text-align:right">${esc(formatUsd(r.value))}</td><td style="text-align:right">${esc(
              formatNumber(r.netWeight),
            )}</td></tr>`,
        )
        .join('');

    const html = `<!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Detailed report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; }
            h1 { margin: 0 0 8px; }
            .meta { color: #444; font-size: 12px; margin-bottom: 16px; }
            .kpis { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin: 16px 0; }
            .kpi { border: 1px solid #ddd; border-radius: 8px; padding: 12px; }
            .kpi .label { color: #666; font-size: 12px; }
            .kpi .value { font-size: 18px; font-weight: bold; margin-top: 4px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #ddd; padding: 8px; font-size: 12px; }
            th { background: #f5f5f5; text-align: left; }
            .two { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
            .brand { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
            .brand-left { display: flex; align-items: center; gap: 10px; }
            .brand-name { font-weight: 800; font-size: 18px; letter-spacing: 0.2px; }
            .brand-sub { color: #666; font-size: 12px; margin-top: 2px; }
            .brand img { width: 34px; height: 34px; border-radius: 8px; }
            .watermark { position: fixed; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; z-index: 0; }
            .watermark span { font-size: 90px; font-weight: 800; color: rgba(0,0,0,0.06); transform: rotate(-28deg); letter-spacing: 2px; }
            .content { position: relative; z-index: 1; }
            @media print {
              body { padding: 18px; }
              tr { page-break-inside: avoid; }
              thead { display: table-header-group; }
            }
          </style>
        </head>
        <body>
          <div class="watermark"><span>FarmersHub</span></div>
          <div class="content">
            <div class="brand">
              <div class="brand-left">
                <img src="${esc(logoUrl)}" alt="FarmersHub" />
                <div>
                  <div class="brand-name">FarmersHub</div>
                  <div class="brand-sub">Import/Export Detailed report</div>
                </div>
              </div>
              <div class="brand-sub">${esc(new Date().toLocaleString())}</div>
            </div>

            <div class="meta">
              <div><b>Year:</b> ${esc(detailedReportPayload.params?.year)} &nbsp; <b>Reporter:</b> ${esc(detailedReportPayload.params?.reporter)} &nbsp; <b>Commodity:</b> ${esc(
                detailedReportPayload.params?.commodity,
              )}</div>
              <div><b>Source:</b> ${esc(detailedReportPayload.meta?.source)} &nbsp; <b>Exports status:</b> ${esc(
                detailedReportPayload.meta?.exportsStatus,
              )} &nbsp; <b>Imports status:</b> ${esc(detailedReportPayload.meta?.importsStatus)}</div>
            </div>

            <div class="kpis">
              <div class="kpi"><div class="label">Exports (USD)</div><div class="value">${esc(formatUsd(report.totals.exportsValue))}</div></div>
              <div class="kpi"><div class="label">Imports (USD)</div><div class="value">${esc(formatUsd(report.totals.importsValue))}</div></div>
              <div class="kpi"><div class="label">Trade balance (USD)</div><div class="value">${esc(formatUsd(report.totals.exportsValue - report.totals.importsValue))}</div></div>
            </div>

            <div class="two">
              <div>
                <h3>Export destinations (all)</h3>
                <table>
                  <thead><tr><th>Destination</th><th style="text-align:right">Value</th><th style="text-align:right">Net weight</th></tr></thead>
                  <tbody>${makeRows(report.topExports)}</tbody>
                </table>
              </div>
              <div>
                <h3>Import sources (all)</h3>
                <table>
                  <thead><tr><th>Source</th><th style="text-align:right">Value</th><th style="text-align:right">Net weight</th></tr></thead>
                  <tbody>${makeRows(report.topImports)}</tbody>
                </table>
              </div>
            </div>
          </div>
        </body>
      </html>`;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (!doc) {
      iframe.remove();
      return;
    }

    doc.open();
    doc.write(html);
    doc.close();

    const win = iframe.contentWindow;
    if (!win) {
      iframe.remove();
      return;
    }

    win.focus();
    setTimeout(() => {
      win.print();
      setTimeout(() => iframe.remove(), 500);
    }, 150);
  };

  // Handle unit change
  const handleUnitChange = (event: any) => {
    setSelectedUnit(event.target.value as keyof typeof UNITS);
  };

  // Handle year range change
  const handleYearRangeChange = (event: Event, newValue: number | number[]) => {
    const rangeValue = newValue as [number, number];
    setYearRange(rangeValue);
    loadTimeSeriesData(rangeValue[0], rangeValue[1]);
  };

  // Handle navigation through time series data
  const handleTimeRangeBack = () => {
    if (visibleTimeRange[0] > 0) {
      const newStart = Math.max(0, visibleTimeRange[0] - 5);
      setVisibleTimeRange([newStart, newStart + 10]);
    }
  };

  const handleTimeRangeForward = () => {
    const maxIndex = getTimeLabels().length;
    if (visibleTimeRange[1] < maxIndex) {
      const newStart = Math.min(maxIndex - 10, visibleTimeRange[0] + 5);
      setVisibleTimeRange([newStart, Math.min(newStart + 10, maxIndex)]);
    }
  };

  const getTimeLabels = (): string[] => {
    if (!timeSeriesData) return [];
    return timeSeriesData.yearly.labels;
  };

  // Get visible time series data based on selected range
  const getVisibleTimeSeriesData = useCallback((): { labels: string[], exports: number[], imports: number[] } => {
    if (!timeSeriesData) {
      return { labels: [], exports: [], imports: [] };
    }

    const data = timeSeriesData.yearly;
    const start = visibleTimeRange[0];
    const end = Math.min(visibleTimeRange[1], data.labels.length);

    return {
      labels: data.labels.slice(start, end),
      exports: data.exports.slice(start, end),
      imports: data.imports.slice(start, end)
    };
  }, [timeSeriesData, visibleTimeRange]);

  // Convert values based on selected unit
  const convertToSelectedUnit = useCallback((value: number): number => {
    if (selectedUnit === 'TONNES') {
      return value * UNITS.TONNES;
    }
    return value;
  }, [selectedUnit]);

  // Format values for display based on selected unit
  const formatValue = (value: number): string => {
    const convertedValue = convertToSelectedUnit(value);
    
    if (selectedUnit === 'USD') {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(convertedValue);
    } else if (selectedUnit === 'TONNES') {
      return `${formatNumber(convertedValue)} tonnes`;
    } else {
      return `${formatNumber(convertedValue)} kg`;
    }
  };

  // Load country data
  const loadCountryData = async (year: number) => {
    setLoading(true);
    try {
      const data = await importExportService.getCountryTradeAnalysis(year);
      setCountryData(data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load country data. Please try again later.');
      setLoading(false);
    }
  };

  // Load commodity data
  const loadCommodityData = async (commodity: string, year: number) => {
    setLoading(true);
    try {
      const data = await importExportService.getCommodityTradeData(commodity, year);
      setCommodityData(data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load commodity data. Please try again later.');
      setLoading(false);
    }
  };

  // Load time series data
  const loadTimeSeriesData = async (startYear: number, endYear: number) => {
    setLoading(true);
    try {
      const data = await importExportService.getTimeSeriesData(startYear, endYear);
      setTimeSeriesData(data);
      
      // Reset visible range when loading new data
      const totalLabels = data.yearly.labels.length;
      setVisibleTimeRange([0, totalLabels > 10 ? 10 : totalLabels]);
      
      setLoading(false);
      renderCharts();
    } catch (err) {
      setError('Failed to load time series data. Please try again later.');
      setLoading(false);
    }
  };

  // Render charts
  const renderCharts = useCallback(() => {
    if (!timeSeriesData) return;
    
    // Get visible data
    const visibleData = getVisibleTimeSeriesData();
    
    // Apply unit conversion to data if needed
    const convertedExports = visibleData.exports.map(value => convertToSelectedUnit(value));
    const convertedImports = visibleData.imports.map(value => convertToSelectedUnit(value));
    
    const chartData = {
      labels: visibleData.labels,
      datasets: [
        {
          label: 'Exports',
          data: convertedExports,
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.1
        },
        {
          label: 'Imports',
          data: convertedImports,
          borderColor: 'rgba(255, 99, 132, 1)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          tension: 0.1
        }
      ]
    };
    
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: selectedUnit === 'USD' ? 'Value (USD)' : `Quantity (${selectedUnit})`
          }
        },
        x: {
          title: {
            display: true,
            text: 'Year'
          }
        }
      }
    };
    
    setChartData(chartData);
    setChartOptions(chartOptions);
  }, [timeSeriesData, selectedUnit, visibleTimeRange, convertToSelectedUnit, getVisibleTimeSeriesData]);

  // Effect to render charts when data or visualization options change
  useEffect(() => {
    if (timeSeriesData) {
      renderCharts();
    }
  }, [timeSeriesData, renderCharts]);

  // Calculate min and max years with fallback to prevent errors
  const minYear = availableYears.length > 0 ? Math.min(...availableYears) : yearRange[0];
  const maxYear = availableYears.length > 0 ? Math.max(...availableYears) : yearRange[1];

  // Render pie chart for top export destinations
  const renderExportsPieChart = () => {
    if (!commodityData || !commodityData.exports || commodityData.exports.length === 0) {
      return <Typography variant="body2" color="textSecondary">No export data available</Typography>;
    }

    // Get top 5 export destinations plus "Others"
    const topExports = commodityData.exports.slice(0, 5);
    const totalTopExportValue = topExports.reduce((sum: number, item: ExportData) => sum + item.value, 0);
    const otherExportsValue = commodityData.exports.slice(5).reduce((sum: number, item: ExportData) => sum + item.value, 0);
    
    // Prepare chart data
    const destinations = [...topExports.map((item: ExportData) => item.destination)];
    if (otherExportsValue > 0) destinations.push('Others');
    
    const values = [...topExports.map((item: ExportData) => item.value)];
    if (otherExportsValue > 0) values.push(otherExportsValue);
    
    // Chart colors
    const backgroundColors = [
      'rgba(75, 192, 192, 0.7)',
      'rgba(54, 162, 235, 0.7)',
      'rgba(153, 102, 255, 0.7)',
      'rgba(255, 159, 64, 0.7)',
      'rgba(255, 99, 132, 0.7)',
      'rgba(201, 203, 207, 0.7)'
    ];
    
    const pieData: ChartData<'pie'> = {
      labels: destinations,
      datasets: [{
        data: values,
        backgroundColor: backgroundColors.slice(0, destinations.length),
        borderWidth: 1,
        borderColor: '#ffffff'
      }]
    };
    
    const options: ChartOptions<'pie'> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
          labels: {
            boxWidth: 12,
            font: {
              size: 10
            }
          }
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.raw as number;
              const totalValue = values.reduce((a: number, b: number) => a + b, 0);
              const percentage = ((value / totalValue) * 100).toFixed(1);
              return `${context.label}: ${formatValue(value)} (${percentage}%)`;
            }
          }
        }
      }
    };
    
    return <Pie data={pieData} options={options} />;
  };
  
  // Render pie chart for top import sources
  const renderImportsPieChart = () => {
    if (!commodityData || !commodityData.imports || commodityData.imports.length === 0) {
      return <Typography variant="body2" color="textSecondary">No import data available</Typography>;
    }

    // Get top 5 import sources plus "Others"
    const topImports = commodityData.imports.slice(0, 5);
    const totalTopImportValue = topImports.reduce((sum: number, item: ImportData) => sum + item.value, 0);
    const otherImportsValue = commodityData.imports.slice(5).reduce((sum: number, item: ImportData) => sum + item.value, 0);
    
    // Prepare chart data
    const sources = [...topImports.map((item: ImportData) => item.source)];
    if (otherImportsValue > 0) sources.push('Others');
    
    const values = [...topImports.map((item: ImportData) => item.value)];
    if (otherImportsValue > 0) values.push(otherImportsValue);
    
    // Chart colors
    const backgroundColors = [
      'rgba(255, 99, 132, 0.7)',
      'rgba(255, 159, 64, 0.7)',
      'rgba(255, 205, 86, 0.7)',
      'rgba(75, 192, 192, 0.7)',
      'rgba(54, 162, 235, 0.7)',
      'rgba(201, 203, 207, 0.7)'
    ];
    
    const pieData: ChartData<'pie'> = {
      labels: sources,
      datasets: [{
        data: values,
        backgroundColor: backgroundColors.slice(0, sources.length),
        borderWidth: 1,
        borderColor: '#ffffff'
      }]
    };
    
    const options: ChartOptions<'pie'> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
          labels: {
            boxWidth: 12,
            font: {
              size: 10
            }
          }
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.raw as number;
              const totalValue = values.reduce((a: number, b: number) => a + b, 0);
              const percentage = ((value / totalValue) * 100).toFixed(1);
              return `${context.label}: ${formatValue(value)} (${percentage}%)`;
            }
          }
        }
      }
    };
    
    return <Pie data={pieData} options={options} />;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Import/Export Data Analysis
      </Typography>
      
      <Paper sx={{ p: 2, mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab icon={<ShowChartIcon />} label="Trade Overview" />
          <Tab icon={<InventoryIcon />} label="Commodity Analysis" />
          <Tab icon={<GavelIcon />} label="Trade Regulations" />
          <Tab icon={<TrendingUpIcon />} label="Market Opportunities" />
        </Tabs>
      </Paper>
      
      {/* Search Filters */}
      {tabValue !== 3 && (
      <Paper sx={{ p: 2, mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          {tabValue === 0 && (
            <>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle1">Country Overview</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="year-select-label">Year</InputLabel>
                  <Select
                    labelId="year-select-label"
                    value={availableYears.includes(selectedYear) ? selectedYear : ''}
                    label="Year"
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                  >
                    {availableYears.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="outlined"
                  onClick={() => handleDetailedReport()}
                  fullWidth
                  disabled={detailedReportLoading}
                >
                  Detailed report
                </Button>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefreshData}
                  fullWidth
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  View overall import/export data for Kenya
                </Typography>
              </Grid>
            </>
          )}
          
          {tabValue === 1 && (
            <>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  options={commodities}
                  getOptionLabel={(option) => option.label}
                  value={commodities.find(c => c.value === commodityQuery) || null}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      setCommodityQuery(newValue.value);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Commodity" variant="outlined" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="year-select-label">Year</InputLabel>
                  <Select
                    labelId="year-select-label"
                    value={availableYears.includes(selectedYear) ? selectedYear : ''}
                    label="Year"
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                  >
                    {availableYears.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  fullWidth
                >
                  Search
                </Button>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="outlined"
                  onClick={() => handleDetailedReport()}
                  fullWidth
                  disabled={detailedReportLoading}
                >
                  Detailed report
                </Button>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefreshData}
                  fullWidth
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  Review reported import and export records for the selected commodity
                </Typography>
              </Grid>
            </>
          )}
          
          {tabValue === 2 && (
            <>
              <Grid item xs={12} md={9}>
                <Typography variant="subtitle1">
                  Trade Regulations Overview
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  View import/export regulations, documentation requirements, and compliance information for Kenya
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>
      )}
      
      {/* Data Visualization Options */}
      {tabValue !== 2 && tabValue !== 3 && (
        <Paper sx={{ p: 2, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Data Visualization Options
          </Typography>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Source Granularity
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Annual only. Quarterly and monthly views are hidden until we have
                source-backed lower-granularity trade series.
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Unit of Measurement
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel id="unit-select-label">Unit</InputLabel>
                <Select
                  labelId="unit-select-label"
                  value={selectedUnit}
                  label="Unit"
                  onChange={handleUnitChange}
                >
                  <MenuItem value="KG">Kilograms</MenuItem>
                  <MenuItem value="TONNES">Tonnes</MenuItem>
                  <MenuItem value="USD">Value (USD)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Year Range
              </Typography>
              <Slider
                value={yearRange}
                onChange={handleYearRangeChange}
                valueLabelDisplay="auto"
                min={minYear}
                max={maxYear}
                marks
                sx={{ mt: 2 }}
                disabled={availableYears.length === 0}
              />
            </Grid>
          </Grid>
        </Paper>
      )}
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 2, mb: 4, bgcolor: 'error.light' }}>
          <Typography color="error" variant="body1">
            {error}
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Tab Content */}
          <Box role="tabpanel" hidden={tabValue !== 0} sx={{ mb: 4 }}>
            {tabValue === 0 && countryData && (
              <Grid container spacing={3}>
                {/* Trade Overview */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader title="Trade Overview" />
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Total Exports
                          </Typography>
                          <Typography variant="h6" color="success.main">
                            {formatValue(countryData.totalExportValue)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Top reported destinations: {countryData.topExports.length}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Total Imports
                          </Typography>
                          <Typography variant="h6" color="error.main">
                            {formatValue(countryData.totalImportValue)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Top reported sources: {countryData.topImports.length}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Trade Balance
                          </Typography>
                          <Typography 
                            variant="h6" 
                            color={countryData.tradeBalance >= 0 ? 'success.main' : 'error.main'}
                          >
                            {formatValue(countryData.tradeBalance)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {countryData.tradeBalance >= 0 ? 'Trade surplus' : 'Trade deficit'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="textSecondary">
                            YoY Growth
                          </Typography>
                          <Typography variant="h6">
                            {typeof countryData.yearOnYearGrowth === 'number'
                              ? `${countryData.yearOnYearGrowth.toFixed(1)}%`
                              : 'Unavailable'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Current overview source does not provide a verified year-on-year calculation here.
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Top Exports */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader 
                      title="Top Exports" 
                      subheader="Largest reported export flows by value"
                    />
                    <CardContent>
                      {countryData.topExports.slice(0, 3).map((exportItem, index) => (
                        <Box key={index} mb={index !== 2 ? 2 : 0}>
                          <Grid container spacing={1}>
                            <Grid item xs={8}>
                              <Typography variant="body2">
                                {exportItem.commodity} to {exportItem.destination}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                Reported quantity: {formatNumber(exportItem.quantity)}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2" align="right">
                                {formatValue(exportItem.value)}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" align="right" display="block">
                                Destination rank #{index + 1}
                              </Typography>
                            </Grid>
                          </Grid>
                          {index !== 2 && <Divider sx={{ mt: 1 }} />}
                        </Box>
                      ))}
                      <Box mt={2}>
                        <Typography variant="caption" color="textSecondary">
                          Additional reported destinations: {countryData.topExports.slice(3).map((item) => item.destination).join(', ') || 'None'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Trade Trends Chart */}
                <Grid item xs={12}>
                  <Card>
                    <CardHeader 
                      title="Trade Trends"
                      subheader="Annual World Bank trade series"
                      action={
                        <Box display="flex" alignItems="center">
                          <Tooltip title="View previous data">
                            <span>
                              <IconButton 
                                onClick={handleTimeRangeBack}
                                disabled={visibleTimeRange[0] <= 0}
                              >
                                <NavigateBeforeIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="View more recent data">
                            <span>
                              <IconButton 
                                onClick={handleTimeRangeForward}
                                disabled={visibleTimeRange[1] >= getTimeLabels().length}
                              >
                                <NavigateNextIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      }
                    />
                    <CardContent>
                      <Box height={400}>
                        {chartData && chartOptions ? (
                          <Line data={chartData} options={chartOptions} />
                        ) : (
                          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                            <Typography color="textSecondary">No chart data available</Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Box>
          
          <Box role="tabpanel" hidden={tabValue !== 1} sx={{ mb: 4 }}>
            {tabValue === 1 && commodityData && (
              <Grid container spacing={3}>
                {/* Commodity Overview */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader title={`${commodityQuery.charAt(0).toUpperCase() + commodityQuery.slice(1)} Trade Analysis`} />
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Total Exports
                          </Typography>
                          <Typography variant="h6" color="success.main">
                            {formatValue(commodityData.analysis.totalExportValue)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Reported export destinations: {commodityData.exports.length}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Total Imports
                          </Typography>
                          <Typography variant="h6" color="error.main">
                            {formatValue(commodityData.analysis.totalImportValue)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Reported import sources: {commodityData.imports.length}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Trade Balance
                          </Typography>
                          <Typography 
                            variant="h6" 
                            color={commodityData.analysis.tradeBalance >= 0 ? 'success.main' : 'error.main'}
                          >
                            {formatValue(commodityData.analysis.tradeBalance)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {commodityData.analysis.tradeBalance >= 0 ? 
                              `Net ${commodityQuery} exporter` : 
                              `Net ${commodityQuery} importer`}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="textSecondary">
                            YoY Change
                          </Typography>
                          <Typography variant="h6">
                            {typeof commodityData.analysis.yearOnYearChange === 'number'
                              ? `${commodityData.analysis.yearOnYearChange.toFixed(1)}%`
                              : 'Unavailable'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Current commodity view does not yet calculate a verified year-on-year change from source-backed prior-year records.
                          </Typography>
                        </Grid>
                        <Grid item xs={12} mt={1}>
                          <Typography variant="caption" color="textSecondary">
                            Export volume reported: {formatNumber(commodityData.analysis.exportVolume)}. Import volume reported: {formatNumber(commodityData.analysis.importVolume)}.
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Top Export Destinations */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader 
                      title="Top Export Destinations" 
                      subheader={`Largest reported destinations for ${commodityQuery}`}
                    />
                    <CardContent>
                      {commodityData.exports.slice(0, 3).map((exportItem: ExportData, index: number) => (
                        <Box key={index} mb={index !== 2 ? 2 : 0}>
                          <Grid container spacing={1}>
                            <Grid item xs={8}>
                              <Typography variant="body2">
                                {exportItem.destination}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                Reported quantity: {formatNumber(exportItem.quantity)} {exportItem.unit}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2" align="right">
                                {formatValue(exportItem.value)}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                color="textSecondary" 
                                align="right" 
                                display="block"
                              >
                                Destination rank #{index + 1}
                              </Typography>
                            </Grid>
                          </Grid>
                          {index !== 2 && <Divider sx={{ mt: 1 }} />}
                        </Box>
                      ))}
                      <Box mt={2}>
                        <Typography variant="caption" color="textSecondary">
                          Refer to the Trade Regulations tab for the current compliance guidance tied to Kenyan import and export workflows.
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Commodity Trade Trends Chart */}
                <Grid item xs={12}>
                  <Card>
                    <CardHeader 
                      title={`${commodityQuery.charAt(0).toUpperCase() + commodityQuery.slice(1)} Trade Trends`}
                      subheader="Annual World Bank trade series"
                      action={
                        <Box display="flex" alignItems="center">
                          <Tooltip title="View previous data">
                            <span>
                              <IconButton 
                                onClick={handleTimeRangeBack}
                                disabled={visibleTimeRange[0] <= 0}
                              >
                                <NavigateBeforeIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="View more recent data">
                            <span>
                              <IconButton 
                                onClick={handleTimeRangeForward}
                                disabled={visibleTimeRange[1] >= getTimeLabels().length}
                              >
                                <NavigateNextIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      }
                    />
                    <CardContent>
                      <Box height={400}>
                        {chartData && chartOptions ? (
                          <Line data={chartData} options={chartOptions} />
                        ) : (
                          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                            <Typography color="textSecondary">No chart data available</Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Pie Charts for Export Destinations and Import Sources */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader 
                      title="Top Export Destinations" 
                      subheader="Reported export value split by destination"
                    />
                    <CardContent>
                      <Box height={300}>
                        {renderExportsPieChart()}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader 
                      title="Top Import Sources" 
                      subheader="Reported import value split by source"
                    />
                    <CardContent>
                      <Box height={300}>
                        {renderImportsPieChart()}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Box>
          
          <Box role="tabpanel" hidden={tabValue !== 2} sx={{ mb: 4 }}>
            {tabValue === 2 && countryData && (
              <Grid container spacing={3}>
                {/* Trade Regulations */}
                <Grid item xs={12}>
                  <Card>
                    <CardHeader 
                      title="Import/Export Regulations" 
                      subheader="Key trading requirements and documentation"
                    />
                    <CardContent>
                      <Grid container spacing={3}>
                        {countryData.regulations.map((regulation, index) => (
                          <Grid item xs={12} key={index}>
                            <Card variant="outlined">
                              <CardContent>
                                <Typography variant="h6" gutterBottom>
                                  {regulation.category}
                                </Typography>
                                <Typography variant="body2" paragraph>
                                  {regulation.description}
                                </Typography>
                                <Typography variant="subtitle2" gutterBottom>
                                  Required Documents:
                                </Typography>
                                <ul>
                                  {regulation.documentRequired.map((doc, i) => (
                                    <li key={i}>
                                      <Typography variant="body2">{doc}</Typography>
                                    </li>
                                  ))}
                                </ul>
                                <Box mt={2}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Compliance Requirements:
                                  </Typography>
                                  <Typography variant="body2">
                                    {regulation.category.includes('Import') 
                                      ? 'Imports must comply with Kenyan Bureau of Standards specifications and may be subject to import duties and taxes as per the East African Community Customs Management Act.' 
                                      : 'Exports must meet quality standards, phytosanitary requirements, and may require certificates of origin to qualify for preferential tariff treatment under trade agreements.'}
                                  </Typography>
                                </Box>
                                <Box mt={2}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Relevant Authorities:
                                  </Typography>
                                  <Typography variant="body2">
                                    {regulation.category.includes('Import') 
                                      ? 'Kenya Revenue Authority (KRA), Kenya Bureau of Standards (KEBS), Kenya Plant Health Inspectorate Service (KEPHIS)' 
                                      : 'Kenya Export Promotion and Branding Agency (KEPROBA), Agriculture and Food Authority (AFA), Kenya Plant Health Inspectorate Service (KEPHIS)'}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="textSecondary">
                                  Last Updated: {regulation.lastUpdated}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Box>

          <Box role="tabpanel" hidden={tabValue !== 3} sx={{ mb: 4 }}>
            {tabValue === 3 && <MarketOpportunities />}
          </Box>
        </>
      )}
      
      {/* Data Source */}
      {!loading && !error && (countryData || commodityData) && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="caption" color="textSecondary">
            Data Source: {countryData?.dataSource || 'Kenya National Bureau of Statistics'}
          </Typography>
        </Paper>
      )}

      <Dialog open={loginPromptOpen} onClose={() => setLoginPromptOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Login required</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            Detailed reports are available to logged-in users so we can personalize results and unlock the full platform features.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoginPromptOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setLoginPromptOpen(false);
              navigate('/login', { state: { from: location }, replace: false });
            }}
          >
            Log in
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailedReportOpen}
        onClose={() => setDetailedReportOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Detailed report</DialogTitle>
        <DialogContent>
          {detailedReportLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : detailedReportError ? (
            <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
              <Typography color="error">{detailedReportError}</Typography>
            </Paper>
          ) : detailedReportPayload ? (
            (() => {
              const rows = (Array.isArray(detailedReportPayload.dataset) ? detailedReportPayload.dataset : []) as DetailedRow[];
              const report = buildDetailedReport(rows);
              const balance = report.totals.exportsValue - report.totals.importsValue;
              return (
                <Box>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={4}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="caption" color="textSecondary">
                            Exports (USD)
                          </Typography>
                          <Typography variant="h6">{formatUsd(report.totals.exportsValue)}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Rows: {report.exportsRows.length}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="caption" color="textSecondary">
                            Imports (USD)
                          </Typography>
                          <Typography variant="h6">{formatUsd(report.totals.importsValue)}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Rows: {report.importsRows.length}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="caption" color="textSecondary">
                            Trade balance (USD)
                          </Typography>
                          <Typography variant="h6">{formatUsd(balance)}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Source: {String(detailedReportPayload.meta?.source || 'UN Comtrade')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="textSecondary">
                          Year: <b>{String(detailedReportPayload.params?.year)}</b> • Commodity: <b>{String(detailedReportPayload.params?.commodity)}</b>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="textSecondary">
                          Exports status: <b>{String(detailedReportPayload.meta?.exportsStatus)}</b> • Imports status: <b>{String(detailedReportPayload.meta?.importsStatus)}</b>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom>
                        Top export destinations
                      </Typography>
                      <Paper variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Destination</TableCell>
                              <TableCell align="right">Value (USD)</TableCell>
                              <TableCell align="right">Net Wgt</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {report.topExports.slice(0, 12).map((r) => (
                              <TableRow key={`x-${r.partner}`}>
                                <TableCell>{r.partner}</TableCell>
                                <TableCell align="right">{formatUsd(r.value)}</TableCell>
                                <TableCell align="right">{formatNumber(r.netWeight)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom>
                        Top import sources
                      </Typography>
                      <Paper variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Source</TableCell>
                              <TableCell align="right">Value (USD)</TableCell>
                              <TableCell align="right">Net Wgt</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {report.topImports.slice(0, 12).map((r) => (
                              <TableRow key={`m-${r.partner}`}>
                                <TableCell>{r.partner}</TableCell>
                                <TableCell align="right">{formatUsd(r.value)}</TableCell>
                                <TableCell align="right">{formatNumber(r.netWeight)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Raw rows (preview)
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      Showing first {Math.min(100, rows.length)} of {rows.length} rows. Use the Excel download for more.
                    </Typography>
                    <Paper variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Flow</TableCell>
                            <TableCell>Partner</TableCell>
                            <TableCell align="right">Value (USD)</TableCell>
                            <TableCell align="right">Net Wgt</TableCell>
                            <TableCell align="right">Qty</TableCell>
                            <TableCell>Unit</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.slice(0, 100).map((r, idx) => (
                            <TableRow key={`${r.rgDesc || 'r'}-${r.partnerCode || 'p'}-${idx}`}>
                              <TableCell>{r.rgDesc}</TableCell>
                              <TableCell>{r.ptTitle}</TableCell>
                              <TableCell align="right">{formatUsd(typeof r.TradeValue === 'number' ? r.TradeValue : 0)}</TableCell>
                              <TableCell align="right">{formatNumber(typeof r.NetWeight === 'number' ? r.NetWeight : 0)}</TableCell>
                              <TableCell align="right">{formatNumber(typeof r.qty === 'number' ? r.qty : 0)}</TableCell>
                              <TableCell>{r.qtDesc}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Box>
                </Box>
              );
            })()
          ) : (
            <Typography variant="body2" color="textSecondary">
              No report data available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailedReportOpen(false)}>Close</Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => handleDetailedReport(true)}
            disabled={detailedReportLoading}
          >
            Refresh
          </Button>
          <Button variant="outlined" onClick={printDetailedReportPdf} disabled={!detailedReportPayload}>
            Print / Save PDF
          </Button>
          <Button variant="contained" onClick={downloadDetailedReportExcel} disabled={!detailedReportPayload}>
            Download CSV
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ImportExportAnalysis;
